import { useState } from 'react'
import { useNavigate, Link } from 'react-router'
import {
  login, loginMFA, loginMFASendEmail,
  loginMFASetupTOTP, loginVerifyMFASetupTOTP, loginMFASetupEmail, loginVerifyMFASetupEmail,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import TOTPQRCode from '../components/TOTPQRCode'

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 32,
  maxWidth: 360,
  margin: '80px auto',
}

const inputSt: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  marginBottom: 12,
}

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 14px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  background: '#2563eb',
  color: '#fff',
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { setSession } = useAuth()
  const navigate = useNavigate()

  // Set once the password check passes but the account has MFA enabled —
  // switches the form to the code-entry step instead of completing login.
  // activeMethod is whichever challenge is currently shown — starts as the
  // account's preferred method but can switch to a fallback (e.g. email)
  // if the operator can't use their authenticator app right now.
  const [mfa, setMfa] = useState<{
    token: string
    activeMethod: 'email' | 'totp'
    totpEnabled: boolean
    emailEnabled: boolean
    warning?: string
  } | null>(null)
  const [code, setCode] = useState('')
  const [switchingMethod, setSwitchingMethod] = useState(false)

  // Set once the password check passes but MFA isn't set up on this account
  // yet — MFA is mandatory for every user, so login can't complete until
  // they enroll right now, choosing either method.
  const [enrollToken, setEnrollToken] = useState<string | null>(null)
  const [enrollStep, setEnrollStep] = useState<
    null | { method: 'totp'; secret: string; otpauthUrl: string } | { method: 'email'; warning?: string }
  >(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.mfaRequired && result.mfaToken && result.method) {
        setMfa({
          token: result.mfaToken,
          activeMethod: result.method,
          totpEnabled: !!result.totpEnabled,
          emailEnabled: !!result.emailEnabled,
          warning: result.emailWarning,
        })
      } else if (result.mfaSetupRequired && result.mfaToken) {
        setEnrollToken(result.mfaToken)
      } else if (result.token && result.user) {
        setSession(result.token, result.user)
        navigate('/')
      } else {
        setError('Unexpected response from server.')
      }
    } catch {
      setError('Invalid email or password.')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfa) return
    setError(null)
    setLoading(true)
    try {
      const { token, user } = await loginMFA(mfa.token, code.trim(), mfa.activeMethod)
      setSession(token, user)
      navigate('/')
    } catch {
      setError('Invalid or expired code.')
    } finally {
      setLoading(false)
    }
  }

  // switchToEmailFallback lets an operator who can't use their authenticator
  // app right now (lost/wiped phone) fall back to email — only offered when
  // the account actually has email MFA enabled too.
  const switchToEmailFallback = async () => {
    if (!mfa) return
    setError(null)
    setSwitchingMethod(true)
    try {
      const res = await loginMFASendEmail(mfa.token)
      setMfa({ ...mfa, activeMethod: 'email', warning: res.sent ? undefined : (res.warning ?? 'Code failed to send.') })
      setCode('')
    } catch {
      setError('Could not send email code — try signing in again.')
    } finally {
      setSwitchingMethod(false)
    }
  }

  const switchToAuthenticatorApp = () => {
    if (!mfa) return
    setMfa({ ...mfa, activeMethod: 'totp', warning: undefined })
    setCode('')
    setError(null)
  }

  const startTotpEnrollment = async () => {
    if (!enrollToken) return
    setError(null)
    setLoading(true)
    try {
      const res = await loginMFASetupTOTP(enrollToken)
      setEnrollStep({ method: 'totp', secret: res.secret, otpauthUrl: res.otpauthUrl })
      setCode('')
    } catch {
      setError('Could not start MFA setup — try signing in again.')
    } finally {
      setLoading(false)
    }
  }

  const startEmailEnrollment = async () => {
    if (!enrollToken) return
    setError(null)
    setLoading(true)
    try {
      const res = await loginMFASetupEmail(enrollToken)
      setEnrollStep({ method: 'email', warning: res.sent ? undefined : (res.warning ?? 'Code failed to send.') })
      setCode('')
    } catch {
      setError('Could not start MFA setup — try signing in again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEnrollVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!enrollToken || !enrollStep) return
    setError(null)
    setLoading(true)
    try {
      const { token, user } = enrollStep.method === 'totp'
        ? await loginVerifyMFASetupTOTP(enrollToken, code.trim())
        : await loginVerifyMFASetupEmail(enrollToken, code.trim())
      setSession(token, user)
      navigate('/')
    } catch {
      setError('Invalid or expired code.')
    } finally {
      setLoading(false)
    }
  }

  if (enrollToken) {
    return (
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Set up two-factor authentication</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
          Required for every account before you can continue.
        </p>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}

        {!enrollStep && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button style={btnStyle} onClick={startTotpEnrollment} disabled={loading}>
              Use an Authenticator App
            </button>
            <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={startEmailEnrollment} disabled={loading}>
              Use Email Codes
            </button>
          </div>
        )}

        {enrollStep?.method === 'totp' && (
          <form onSubmit={handleEnrollVerify}>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 8px' }}>
              Scan this QR code with your authenticator app, or enter the secret manually, then
              enter the 6-digit code it shows.
            </p>
            <TOTPQRCode otpauthUrl={enrollStep.otpauthUrl} />
            <div style={{
              background: '#f3f4f6', borderRadius: 6, padding: '10px 14px',
              fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all', marginBottom: 12,
            }}>
              {enrollStep.secret}
            </div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Code</label>
            <input
              style={{ ...inputSt, textAlign: 'center', fontSize: 20, letterSpacing: 4, fontFamily: 'monospace' }}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
            <button style={btnStyle} type="submit" disabled={loading || code.length !== 6}>
              {loading ? 'Verifying…' : 'Verify & Continue'}
            </button>
          </form>
        )}

        {enrollStep?.method === 'email' && (
          <form onSubmit={handleEnrollVerify}>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>
              Enter the 6-digit code we just emailed you.
            </p>
            {enrollStep.warning && (
              <p style={{ color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', fontSize: 13, margin: '0 0 12px' }}>
                {enrollStep.warning}
              </p>
            )}
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Code</label>
            <input
              style={{ ...inputSt, textAlign: 'center', fontSize: 20, letterSpacing: 4, fontFamily: 'monospace' }}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
            <button style={btnStyle} type="submit" disabled={loading || code.length !== 6}>
              {loading ? 'Verifying…' : 'Verify & Continue'}
            </button>
          </form>
        )}

        {enrollStep && (
          <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>
            <a href="#" onClick={(e) => { e.preventDefault(); setEnrollStep(null); setCode(''); setError(null) }}>
              Choose a different method
            </a>
          </p>
        )}
      </div>
    )
  }

  if (mfa) {
    return (
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Verify it's you</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
          {mfa.activeMethod === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter the 6-digit code we just emailed you.'}
        </p>
        {mfa.warning && (
          <p style={{ color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', fontSize: 13, margin: '0 0 12px' }}>
            {mfa.warning}
          </p>
        )}
        <form onSubmit={handleMfaSubmit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Code</label>
          <input
            style={{ ...inputSt, textAlign: 'center', fontSize: 20, letterSpacing: 4, fontFamily: 'monospace' }}
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />
          {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
          <button style={btnStyle} type="submit" disabled={loading || code.length !== 6}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
        </form>
        {mfa.activeMethod === 'totp' && mfa.emailEnabled && (
          <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (!switchingMethod) switchToEmailFallback() }}>
              {switchingMethod ? 'Sending code…' : "Can't access your authenticator app? Use email instead"}
            </a>
          </p>
        )}
        {mfa.activeMethod === 'email' && mfa.totpEnabled && (
          <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>
            <a href="#" onClick={(e) => { e.preventDefault(); switchToAuthenticatorApp() }}>
              Use your authenticator app instead
            </a>
          </p>
        )}
        <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 8 }}>
          <a href="#" onClick={(e) => { e.preventDefault(); setMfa(null); setCode(''); setError(null) }}>
            Back to sign in
          </a>
        </p>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: 20 }}>Sign in</h2>
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Email</label>
        <input style={inputSt} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Password</label>
        <input style={inputSt} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <button style={btnStyle} type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>
        Got an invite email? <Link to="/accept-invite">Set your password here</Link>.
      </p>
    </div>
  )
}
